import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import DataTable, { type Column } from "@/components/DataTable";
import type { Portal, TenantDomain, DomainStatus } from "@/types";
import { usePortalDomains } from "../hooks/usePortalDomains";
import AddDomainDialog from "./domains/AddDomainDialog";
import DomainInstructions from "./domains/DomainInstructions";

// Status chip color mapping
const statusColorMap: Record<
  DomainStatus,
  "default" | "warning" | "info" | "success" | "error"
> = {
  PENDING_OWNERSHIP: "warning",
  PENDING_CERT: "warning",
  DEPLOYING: "info",
  PENDING_DNS: "warning",
  ACTIVE: "success",
  FAILED: "error",
  REMOVED: "default",
};

const statusLabelMap: Record<DomainStatus, string> = {
  PENDING_OWNERSHIP: "Pending Ownership",
  PENDING_CERT: "Pending Certificate",
  DEPLOYING: "Deploying",
  PENDING_DNS: "Pending DNS",
  ACTIVE: "Active",
  FAILED: "Failed",
  REMOVED: "Removed",
};

const terminalStatuses: DomainStatus[] = ["ACTIVE", "REMOVED"];

interface DomainsTabProps {
  portal: Portal;
}

const DomainsTab: React.FC<DomainsTabProps> = ({ portal }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

  const {
    domains,
    isLoading,
    addDomain,
    isAdding,
    verifyDomain,
    isVerifying,
    removeDomain,
    isRemoving,
  } = usePortalDomains(portal.id);

  // Keep selectedDomain in sync with latest query data
  const selectedDomain = selectedDomainId
    ? domains.find((d) => d.id === selectedDomainId) ?? null
    : null;

  const handleAddDomain = (hostname: string) => {
    addDomain(
      { hostname },
      {
        onSuccess: () => {
          setIsAddOpen(false);
        },
      }
    );
  };

  const handleVerify = (domainId: string) => {
    verifyDomain(domainId);
  };

  const handleRemove = (domainId: string) => {
    if (
      window.confirm("Are you sure you want to remove this custom domain?")
    ) {
      removeDomain(domainId, {
        onSuccess: () => {
          // Return to list if viewing the removed domain
          if (selectedDomainId === domainId) {
            setSelectedDomainId(null);
          }
        },
      });
    }
  };

  // Detail view — full page stepper with back button
  if (selectedDomain) {
    return (
      <DomainInstructions
        domain={selectedDomain}
        onBack={() => setSelectedDomainId(null)}
        onVerify={handleVerify}
        isVerifying={isVerifying}
      />
    );
  }

  // List view
  const columns: Column<TenantDomain>[] = [
    {
      id: "hostname",
      label: "Hostname",
      render: (row) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          onClick={() => setSelectedDomainId(row.id)}
        >
          {row.hostname}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Chip
          label={statusLabelMap[row.status]}
          color={statusColorMap[row.status]}
          size="small"
        />
      ),
    },
    {
      id: "lastChecked",
      label: "Last Checked",
      render: (row) =>
        row.last_checked_at
          ? new Date(row.last_checked_at).toLocaleString()
          : "Never",
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <>
          <Tooltip title="Setup details">
            <IconButton
              size="small"
              onClick={() => setSelectedDomainId(row.id)}
              aria-label="setup details"
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!terminalStatuses.includes(row.status) && (
            <Tooltip
              title={
                row.status === "DEPLOYING"
                  ? "Check Status"
                  : "Verify / Re-check"
              }
            >
              <IconButton
                size="small"
                onClick={() => handleVerify(row.id)}
                disabled={isVerifying}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Remove domain">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemove(row.id)}
              disabled={isRemoving}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Box>
      {/* Platform subdomain (read-only) */}
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Typography variant="body1" fontWeight={600}>
          Platform Subdomain
        </Typography>
        <TextField
          label="Subdomain"
          value={portal.settings?.subdomain ?? portal.slug}
          fullWidth
          disabled
          helperText="Managed automatically by the platform"
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>

      {/* Custom domains section */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>
            Custom Domains
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add and manage custom domains for this portal.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddOpen(true)}
        >
          Add Domain
        </Button>
      </Stack>

      {/* Add domain dialog */}
      <AddDomainDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSubmit={handleAddDomain}
        isSubmitting={isAdding}
      />

      {/* Domains table */}
      <DataTable
        columns={columns}
        data={domains}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="No custom domains configured"
        emptyDescription="Add a custom domain to serve this portal on your own hostname."
        sx={{ mt: 3 }}
      />
    </Box>
  );
};

export default DomainsTab;
